// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import QRScanningModal, {
  QRScanningModalProps,
} from 'components/organisms/QRScanningModal';
import Popover from 'components/atoms/Popover';
import { ErrorText } from 'components/atoms/ErrorText';
import { useTranslation } from 'react-i18next';
import { createAxios } from 'ts/createAxios';
import { REGEX_OBJECT } from 'util/ConstantValues';
import { getApiPath, mergeClasses, normalizeNumber } from 'util/commons';
import { API_ENDPOINTS } from 'util/endPoints';
import { OperatorType, ProcessingMode } from 'util/Enums';
import { handleError } from 'util/errorHandler';
import {
  InputResourceQRData,
  InputResourceQRResource,
  PreRegisSelectedResource,
} from 'util/Types';
import styles from './CompressionInputResourceScanModal.module.scss';
import { IconEyeSlash } from 'components/atoms/icons/IconEyeSlash';
import { IconCheck } from 'components/atoms/icons/IconCheck';
import { toast } from 'react-toastify';
import { useState } from 'react';

type ScannedItemData = InputResourceQRResource &
  Omit<InputResourceQRData, 'resources'>;

type CompressionInputResourceScanModalProps = Omit<
  QRScanningModalProps<
    ScannedItemData,
    ScannedItemData & {
      id: string;
    }
  >,
  'onScan' | 'onSubmit' | 'getItemId'
> & {
  onNext?: (values: PreRegisSelectedResource[]) => void;
  processingMode?: ProcessingMode | 'common';
};

const CompressionInputResourceScanModal: React.FC<
  CompressionInputResourceScanModalProps
> = ({ onNext, processingMode, ...rest }) => {
  const { t } = useTranslation();
  type InputWeight = { weight: string; hasError: boolean };
  const [inputWeights, setInputWeights] = useState<Record<string, InputWeight>>(
    {}
  );

  const isManual = processingMode === ProcessingMode.Manual;
  const hasInputWeightError = Object.values(inputWeights).some(
    ({ hasError }) => hasError
  );

  const handleScan = async (
    qrCode: string,
    addItem: (data: ScannedItemData) => void
  ) => {
    try {
      const { data } = await createAxios().get<InputResourceQRData>(
        getApiPath(API_ENDPOINTS.INPUT_RESOURCES_SCAN, {
          operatorType: OperatorType.Compressions,
          qrCode,
        })
      );

      data.resources.forEach((resource) => {
        addItem({
          ...data,
          ...resource,
          weight: normalizeNumber({
            value: resource.weight,
            commaSplit: false,
          }),
          remainingWeight: normalizeNumber({
            value: resource.remainingWeight,
            commaSplit: false,
          }),
        });
      });
    } catch (error) {
      handleError(error);
    }
  };

  const handleChangeInputWeight = (
    value: string,
    item: ScannedItemData & { id: string },
    isChecked: boolean,
    onToggle: () => void
  ) => {
    if (value && !REGEX_OBJECT.numberWithDot2Decimal.test(value)) {
      return;
    }

    if (value && !isChecked) {
      onToggle();
    }

    if (!value && isChecked) {
      onToggle();
    }

    setInputWeights((prev) => ({
      ...prev,
      [item.id]: {
        weight: value,
        hasError: Number(value) > Number(item.remainingWeight),
      },
    }));
  };

  const handleNextClick = (
    checkedItems: (ScannedItemData & { id: string })[]
  ) => {
    if (!checkedItems.length) {
      return;
    }

    const registeringData = checkedItems.map(
      ({ id, processingObjectId, remainingWeight, materialName }) => ({
        processingObjectId,
        weight: isManual
          ? inputWeights[id]?.weight ?? remainingWeight
          : remainingWeight,
        materialName,
      })
    );

    if (
      registeringData.some(
        ({ materialName }) => materialName !== registeringData[0].materialName
      )
    ) {
      toast.error(t('messages.M_please_select_same_material_name'));

      return;
    }

    onNext?.(
      checkedItems.map(
        ({ id, processingObjectId, remainingWeight, materialName }) => ({
          processingObjectId,
          weight: isManual
            ? inputWeights[id]?.weight ?? remainingWeight
            : remainingWeight,
          materialName,
        })
      )
    );
  };

  return (
    <QRScanningModal
      {...rest}
      title={t('pre_regist_resource_select.scan_modal.title') || ''}
      onScan={handleScan}
      onFinishButtonClick={({ checkedItems }) => handleNextClick(checkedItems)}
      renderBottomActions={({ checkedItems, isDisabled }) => (
        <button
          className={styles.finishButton}
          disabled={isDisabled || hasInputWeightError}
          onClick={() => handleNextClick(checkedItems)}
        >
          <div className={styles.finishButtonContent}>
            <div className={styles.finishButtonIcon}>
              <IconCheck size={20} fillColor="#000" borderColor="#000" />
            </div>
            {t('common.button.next')}
          </div>
        </button>
      )}
      getItemId={({ processingObjectId }) => processingObjectId}
      toGroupItem={(data) => ({ ...data, id: data.processingObjectId })}
      getLatestScanDisplay={({ materialName, remainingWeight }) => ({
        name: materialName,
        value: normalizeNumber({ value: remainingWeight }),
        unit: 'kg',
        label: t('common.material_name') as string,
      })}
      renderGroupItem={({ item, isChecked, onToggle }) => {
        const handleToggle = () => {
          if (!isChecked && !inputWeights[item.id]?.weight) {
            setInputWeights((prev) => ({
              ...prev,
              [item.id]: {
                weight: item.remainingWeight,
                hasError: false,
              },
            }));
          }
          onToggle();
        };

        return (
          <div
            className={styles.groupItem}
            onClick={handleToggle}
            key={item.id}
          >
            <div className={styles.groupItemInner}>
              <div className={styles.groupItemInfo}>
                <div className={styles.iconContainer}>
                  {isChecked ? (
                    <div className={styles.iconCheck}>
                      <IconCheck size={20} />
                    </div>
                  ) : (
                    <IconEyeSlash size={26} />
                  )}
                </div>

                <div>
                  <p className={styles.groupMaterialName}>
                    <Popover
                      content={item.materialName}
                      autoGen
                      positions={['top']}
                      containerStyle={{
                        zIndex: '300001',
                      }}
                    >
                      <span>{item.materialName}</span>
                    </Popover>
                  </p>
                  <p className={styles.groupItemId}>
                    <Popover
                      content={item.id}
                      autoGen
                      positions={['top']}
                      containerStyle={{
                        zIndex: '300001',
                      }}
                    >
                      <span>{item.id}</span>
                    </Popover>
                  </p>
                </div>
              </div>

              <div className={styles.groupItemValue}>
                {[
                  {
                    value: isManual ? item.remainingWeight : item.weight,
                    label: isManual
                      ? t('pre_regist_resource_select.weight')
                      : t('pre_regist_resource_select.received_weight'),
                  },
                  {
                    value: item.remainingWeight,
                    label: isManual
                      ? t('pre_regist_resource_select.input_weight')
                      : t('pre_regist_resource_select.processing_capacity'),
                  },
                ].map(({ value, label }, index) => (
                  <div key={index} className={styles.groupItemValueItem}>
                    <p className={styles.valueLabel}>{label}</p>
                    <div className={styles.valueContainer}>
                      {isManual && index === 1 ? (
                        <div className={styles.inputWeightWrapper}>
                          <div className={styles.inputWeightRow}>
                            <input
                              autoFocus
                              type="text"
                              className={mergeClasses(styles.manualInput, {
                                [styles.manualInputError]:
                                  inputWeights[item.id]?.hasError,
                              })}
                              value={
                                inputWeights[item.id]?.weight ??
                                item.remainingWeight
                              }
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                handleChangeInputWeight(
                                  e.target.value,
                                  item,
                                  isChecked,
                                  handleToggle
                                )
                              }
                            />
                            <span className={styles.unit}>kg</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Popover
                            content={`${normalizeNumber({
                              value: value || 0,
                            })}kg`}
                            autoGen
                            positions={['top']}
                            containerStyle={{
                              zIndex: '300001',
                            }}
                          >
                            <p className={styles.value}>
                              {normalizeNumber({ value: value || 0 })}
                            </p>
                          </Popover>
                          <span className={styles.unit}>kg</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {inputWeights[item.id]?.hasError && (
              <ErrorText
                className={styles.errorText}
                text={
                  t('pre_regist_resource_select.message_error', {
                    field: item.remainingWeight,
                  }) || ''
                }
              />
            )}
          </div>
        );
      }}
    />
  );
};

export default CompressionInputResourceScanModal;
